import React from "react";
import { useHistory } from "react-router-dom";
import {
	BsCheckCircle,
	BsDownload,
	BsExclamationTriangle,
	BsXCircle,
} from "react-icons/bs";
import useDownloadBulkImportReport from "api/BulkBookImport/useDownloadBulkImportReport";
import useRetryBulkImport from "api/BulkBookImport/useRetryBulkImport";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";

const SummaryStep = ({ importData }) => {
	const history = useHistory();
	const download = useDownloadBulkImportReport();
	const retry = useRetryBulkImport();
	const counts = importData?.counts || {};
	const status = importData?.status;
	const cancelled = status === "cancelled";
	const withErrors = status === "completed_with_errors";
	const failed = status === "failed";
	const canRetry =
		(status === "completed_with_errors" || status === "failed") &&
		((counts.failed || 0) > 0 || (counts.partial || 0) > 0);
	const hasRows = (counts.total || 0) > 0;

	const tone = cancelled || failed ? "warning" : withErrors ? "warning" : "success";
	const Icon = cancelled || failed ? BsXCircle : withErrors ? BsExclamationTriangle : BsCheckCircle;

	const title = cancelled
		? "Import cancelled"
		: failed
		? "Import failed"
		: withErrors
		? "Finished with some issues"
		: "Import finished";

	const description = cancelled
		? "This import was cancelled. You can download any row report that was generated, or start a new bulk upload."
		: failed
		? "Something went wrong before processing finished. Download the report below or check your email if one was sent."
		: "A results email is also sent when processing finishes. Download the Excel report here anytime. Failed or partial rows can be retried without redoing successful ones.";

	const tiles = [
		{
			label: "Successful",
			value: counts.successful || 0,
			className: "text-akgreenest bg-akgreen bg-opacity-5 border-akgreen border-opacity-20",
		},
		{
			label: "Partial",
			value: counts.partial || 0,
			className: "text-orange-700 bg-orange-50 border-orange-100",
		},
		{
			label: "Skipped",
			value: counts.skipped || 0,
			className: "text-gray-700 bg-gray-50 border-gray-200",
		},
		{
			label: "Failed",
			value: counts.failed || 0,
			className: "text-red-700 bg-red-50 border-red-100",
		},
	];

	const downloadButton = (
		<button
			type="button"
			className="inline-flex portal-control-height items-center justify-center gap-2 rounded-lg bg-akgreen px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-akgreener disabled:opacity-50"
			disabled={download.isLoading || !importData?.id}
			onClick={() => download.mutate(importData.id)}
		>
			<BsDownload className="h-4 w-4" aria-hidden />
			{download.isLoading ? "Downloading…" : "Download report"}
		</button>
	);

	return (
		<StepPanel
			stepNumber={4}
			title={title}
			description={description}
			status={
				<StatusBanner
					tone={tone}
					title={`Status: ${status || "—"}`}
					action={downloadButton}
				>
					<div className="flex items-center gap-2">
						<Icon className="h-4 w-4" aria-hidden />
						<span>
							{cancelled
								? "No further processing will run for this batch."
								: hasRows
								? "Download the full row report (same file as the email attachment)."
								: "Review the counts below. A report downloads once rows exist."}
						</span>
					</div>
				</StatusBanner>
			}
			nav={{
				onBack: () => history.push("/bulk-book-imports"),
				backLabel: "Start another import",
				hideNext: true,
				extra: (
					<>
						{downloadButton}
						{canRetry ? (
							<button
								type="button"
								className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 disabled:opacity-50"
								disabled={retry.isLoading}
								onClick={() => retry.mutate(importData.id)}
							>
								{retry.isLoading ? "Retrying…" : "Retry failed items"}
							</button>
						) : null}
					</>
				),
			}}
		>
			<ul className="grid gap-3 sm:grid-cols-2">
				{tiles.map((tile) => (
					<li
						key={tile.label}
						className={`rounded-xl border p-4 ${tile.className}`}
					>
						<p className="text-xs font-semibold uppercase tracking-wide opacity-70">
							{tile.label}
						</p>
						<p className="mt-1 text-2xl font-semibold tabular-nums">
							{tile.value}
						</p>
					</li>
				))}
			</ul>
		</StepPanel>
	);
};

export default SummaryStep;
